import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { downloadBlob } from "../lib/download";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Switch } from "../components/ui/switch";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Play, Pencil, Trash2, Download, Plus, MailWarning, Loader2 } from "lucide-react";

const TH = "text-left text-[10px] uppercase tracking-wider text-[#6B7280] px-2 py-1.5 border-b border-gray-200";
const TD = "px-2 py-1.5 text-xs text-[#0E2240] border-b border-gray-100";
const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const SEL = "w-full border border-gray-300 rounded-sm px-2 py-1.5 text-sm bg-white";

const freqDesc = (s) => {
  const h = `${String(s.hour).padStart(2, "0")}:00 IST`;
  if (s.frequency === "daily") return `Daily at ${h}`;
  if (s.frequency === "weekly") return `Every ${WEEKDAYS[s.weekday ?? 0]} at ${h}`;
  return `Monthly on day ${s.day_of_month ?? 1} at ${h}`;
};
const fmtDt = (v) => (v ? new Date(v).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");

const EMPTY = { name: "", report_type: "manning_excel", frequency: "weekly", weekday: 0, day_of_month: 1, hour: 8, mode: "Live", recipient_roles: [], enabled: true };

export default function ReportSchedules() {
  const { can } = useAuth();
  const write = can("reports", "write");
  const [meta, setMeta] = useState({ schedules: [], report_types: [], roles: [] });
  const [runs, setRuns] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    api.get("/analytics/reports/schedules").then(({ data }) => setMeta(data)).catch((e) => toast.error(apiError(e)));
    api.get("/analytics/reports/runs").then(({ data }) => setRuns(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (s) => {
    try {
      await api.put(`/analytics/reports/schedules/${s.schedule_id}`, { enabled: !s.enabled });
      toast.success(!s.enabled ? "Schedule enabled" : "Schedule paused");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const runNow = async (s) => {
    setBusy(s.schedule_id);
    try {
      await api.post(`/analytics/reports/schedules/${s.schedule_id}/run-now`);
      toast.success(`"${s.name}" generated — recipients notified (email MOCKED)`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };

  const remove = async () => {
    const s = deleting;
    setDeleting(null);
    try {
      await api.delete(`/analytics/reports/schedules/${s.schedule_id}`);
      toast.success("Schedule deleted");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const save = async () => {
    const body = { ...editing };
    const isNew = !body.schedule_id;
    try {
      if (isNew) await api.post("/analytics/reports/schedules", body);
      else await api.put(`/analytics/reports/schedules/${body.schedule_id}`, body);
      toast.success(isNew ? "Schedule created" : "Schedule updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const download = async (r) => {
    try {
      await downloadBlob(`/analytics/reports/runs/${r.run_id}/download`, `${r.name.replace(/\s+/g, "-").toLowerCase()}.${r.file_ext}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const set = (k, v) => setEditing((e) => ({ ...e, [k]: v }));
  const toggleRole = (role) =>
    setEditing((e) => ({
      ...e,
      recipient_roles: e.recipient_roles.includes(role) ? e.recipient_roles.filter((r) => r !== role) : [...e.recipient_roles, role],
    }));
  const typeLabel = (k) => meta.report_types.find((t) => t.key === k)?.label || k;

  return (
    <div data-testid="report-schedules-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280]">Governance</div>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#0E2240] tracking-tight mt-1">Scheduled Reports</h1>
        </div>
        {write && (
          <Button data-testid="new-schedule-btn" onClick={() => setEditing({ ...EMPTY })}
            className="bg-[#0E2240] hover:bg-[#0E2240]/90 text-white rounded-sm">
            <Plus size={15} className="mr-1.5" /> New schedule
          </Button>
        )}
      </div>

      <div className="flex items-start gap-2.5 bg-[#D97706]/10 border border-[#D97706]/40 rounded-sm px-4 py-3 mb-6" data-testid="mock-email-banner">
        <MailWarning size={16} className="text-[#D97706] mt-0.5 shrink-0" strokeWidth={1.75} />
        <p className="text-xs text-[#6B7280]">
          <span className="font-semibold text-[#0E2240]">Email delivery is MOCKED.</span> When a report runs, the file is generated and stored,
          recipients get an in-app notification, and the mock delivery is logged. Files are downloadable from the run history below.
        </p>
      </div>

      <div className="space-y-3 mb-8">
        {meta.schedules.map((s) => (
          <div key={s.schedule_id} data-testid={`schedule-card-${s.schedule_id}`}
            className={`bg-white border rounded-sm p-4 ${s.enabled ? "border-gray-200" : "border-gray-200 opacity-60"}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-[#0E2240]">{s.name}</span>
                  <span className="text-[10px] uppercase tracking-wider bg-[#F5F6F8] border border-gray-200 rounded-sm px-1.5 py-0.5 text-[#6B7280]">{typeLabel(s.report_type)}</span>
                </div>
                <div className="text-[11px] text-[#6B7280] mt-1 tabular-nums">
                  {freqDesc(s)} · Next run: {fmtDt(s.next_run_on)} · Last run: {fmtDt(s.last_run_on)}
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {s.recipient_roles.map((r) => (
                    <span key={r} className="text-[10px] bg-[#0E2240]/5 border border-[#0E2240]/15 rounded-sm px-1.5 py-0.5 text-[#0E2240]">{r.replace(/_/g, " ")}</span>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {write && (
                  <>
                    <Switch data-testid={`schedule-toggle-${s.schedule_id}`} checked={s.enabled} onCheckedChange={() => toggle(s)} />
                    <button data-testid={`schedule-run-${s.schedule_id}`} onClick={() => runNow(s)} disabled={busy === s.schedule_id}
                      className="flex items-center gap-1 border border-gray-300 rounded-sm px-2.5 py-1.5 text-xs font-medium text-[#0E2240] hover:border-[#0E2240] transition-colors duration-200 disabled:opacity-50">
                      {busy === s.schedule_id ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} className="text-[#1B7F3B]" />} Run now
                    </button>
                    <button data-testid={`schedule-edit-${s.schedule_id}`} onClick={() => setEditing({ ...EMPTY, ...s })}
                      className="p-1.5 text-[#6B7280] hover:text-[#0E2240] transition-colors duration-200"><Pencil size={14} /></button>
                    <button data-testid={`schedule-delete-${s.schedule_id}`} onClick={() => setDeleting(s)}
                      className="p-1.5 text-[#6B7280] hover:text-[#BB162B] transition-colors duration-200"><Trash2 size={14} /></button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
        {meta.schedules.length === 0 && <div className="text-xs text-[#6B7280] py-6 text-center bg-white border border-gray-200 rounded-sm">No schedules configured</div>}
      </div>

      <h2 className="font-serif text-lg text-[#0E2240] mb-3">Run history</h2>
      <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto" data-testid="run-history-table">
        <table className="w-full">
          <thead><tr><th className={TH}>Report</th><th className={TH}>Generated</th><th className={TH}>Trigger</th><th className={TH}>Recipients</th><th className={TH}>Size</th><th className={TH}></th></tr></thead>
          <tbody>
            {(runs || []).map((r) => (
              <tr key={r.run_id}>
                <td className={`${TD} font-medium`}>{r.name}</td>
                <td className={`${TD} tabular-nums`}>{fmtDt(r.generated_on)}</td>
                <td className={TD}>{r.trigger === "manual" ? "Manual" : "Scheduled"}</td>
                <td className={`${TD} tabular-nums`} title={(r.recipients || []).map((u) => u.email).join(", ")}>{(r.recipients || []).length} (email mocked)</td>
                <td className={`${TD} tabular-nums`}>{r.file_size ? `${Math.round(r.file_size / 1024)} KB` : "—"}</td>
                <td className={TD}>
                  {r.file_path ? (
                    <button data-testid={`run-download-${r.run_id}`} onClick={() => download(r)}
                      className="flex items-center gap-1 text-xs text-[#1E5AA8] hover:underline"><Download size={13} /> Download</button>
                  ) : <span className="text-[11px] text-[#6B7280]">Unavailable</span>}
                </td>
              </tr>
            ))}
            {runs && runs.length === 0 && <tr><td colSpan={6} className="px-2 py-8 text-center text-xs text-[#6B7280]">No runs yet — use "Run now" on a schedule</td></tr>}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="delete-schedule-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-[#0E2240]">Delete schedule?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleting?.name}" will stop running and be removed. Past run history and downloaded files remain available.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" data-testid="delete-schedule-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-schedule-confirm" onClick={remove}
              className="bg-[#BB162B] hover:bg-[#BB162B]/90 text-white rounded-sm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="schedule-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-[#0E2240]">{editing?.schedule_id ? "Edit schedule" : "New schedule"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#6B7280]">Name</label>
                <Input data-testid="schedule-name-input" value={editing.name} onChange={(e) => set("name", e.target.value)} className="rounded-sm mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#6B7280]">Report type</label>
                  <select data-testid="schedule-type-select" value={editing.report_type} onChange={(e) => set("report_type", e.target.value)} className={`${SEL} mt-1`}>
                    {meta.report_types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#6B7280]">Frequency</label>
                  <select data-testid="schedule-frequency-select" value={editing.frequency} onChange={(e) => set("frequency", e.target.value)} className={`${SEL} mt-1`}>
                    <option value="daily">Daily</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {editing.frequency === "weekly" && (
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-[#6B7280]">Day of week</label>
                    <select data-testid="schedule-weekday-select" value={editing.weekday ?? 0} onChange={(e) => set("weekday", Number(e.target.value))} className={`${SEL} mt-1`}>
                      {WEEKDAYS.map((w, i) => <option key={w} value={i}>{w}</option>)}
                    </select>
                  </div>
                )}
                {editing.frequency === "monthly" && (
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-[#6B7280]">Day of month (1–28)</label>
                    <Input data-testid="schedule-dom-input" type="number" min={1} max={28} value={editing.day_of_month ?? 1}
                      onChange={(e) => set("day_of_month", Number(e.target.value))} className="rounded-sm mt-1" />
                  </div>
                )}
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#6B7280]">Hour (IST, 0–23)</label>
                  <Input data-testid="schedule-hour-input" type="number" min={0} max={23} value={editing.hour}
                    onChange={(e) => set("hour", Number(e.target.value))} className="rounded-sm mt-1" />
                </div>
                {editing.report_type !== "sla_digest" && (
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-[#6B7280]">Scope</label>
                    <select data-testid="schedule-mode-select" value={editing.mode || "Live"} onChange={(e) => set("mode", e.target.value)} className={`${SEL} mt-1`}>
                      <option value="Live">BAU</option><option value="Greenfield">Greenfield</option><option value="All">All</option>
                    </select>
                  </div>
                )}
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#6B7280]">Recipient roles (mock email + in-app)</label>
                <div className="grid grid-cols-2 gap-1.5 mt-1.5">
                  {meta.roles.map((r) => (
                    <label key={r} className="flex items-center gap-2 text-xs text-[#0E2240] cursor-pointer">
                      <input type="checkbox" data-testid={`schedule-role-${r}`} checked={editing.recipient_roles.includes(r)} onChange={() => toggleRole(r)} className="accent-[#0E2240]" />
                      {r.replace(/_/g, " ")}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setEditing(null)}>Cancel</Button>
            <Button data-testid="schedule-save-btn" onClick={save} className="bg-[#0E2240] hover:bg-[#0E2240]/90 text-white rounded-sm">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
